import React, { useState } from "react";
import axios from "axios";
import {
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverCloseButton, PopoverHeader, PopoverBody,
  Button, Flex, Box, Text, Select, FormControl, FormLabel, Input, InputGroup, InputLeftAddon, useToast, useDisclosure, Spinner, Portal,
} from "@chakra-ui/react";

const API = process.env.REACT_APP_API_URL;

/**
 * The page / retailer / supplier access editors as POPOVERS (spec:
 * users.master-admins.md §then-for-the-2) — no navigation, so the table's
 * scroll position is untouched. Each fetches lazily when opened and saves via
 * the same endpoints the old Edit* pages used. Billing follows the same shape.
 */

const TinyToggle = ({ on, onClick, children }) => (
  <Button size="xs" variant={on ? "solid" : "outline"} colorScheme={on ? "green" : "gray"} mr={1} mb={1} onClick={onClick}>
    {children}
  </Button>
);

// placement: "left" suits the Grants column at the right edge of the table; a
// popover triggered from a column near the LEFT edge must open downwards or it
// lands under the fixed sidebar (the Org column, 2026-09-17).
const GrantPopover = ({ trigger, header, onOpenFetch, onSave, children, width = "340px", placement = "left" }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const open = async () => {
    onOpen();
    if (!onOpenFetch) return;
    setLoading(true);
    try {
      await onOpenFetch();
    } catch (error) {
      toast({ title: "Failed to load", description: error?.response?.data?.message || error?.message, status: "error", duration: 5000, isClosable: true });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave();
      onClose();
    } catch (error) {
      toast({ title: "Failed to save", description: error?.response?.data?.message || error?.message, status: "error", duration: 6000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover isOpen={isOpen} onOpen={open} onClose={onClose} placement={placement} isLazy>
      <PopoverTrigger>{trigger}</PopoverTrigger>
      <Portal>
        {/* zIndex: the sidebar is position:fixed at 1000; the Portal'd popover must sit above it. */}
        <PopoverContent w={width} zIndex={1600}>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontSize="sm" fontWeight="600">{header}</PopoverHeader>
          <PopoverBody>
            {loading ? (
              <Flex justify="center" py={4}><Spinner size="sm" /></Flex>
            ) : (
              <>
                {children}
                <Flex justify="flex-end" gap={2} mt={3}>
                  <Button size="xs" variant="ghost" onClick={onClose}>Cancel</Button>
                  <Button size="xs" colorScheme="teal" onClick={save} isLoading={saving}>Save</Button>
                </Flex>
              </>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

// ── Pages ───────────────────────────────────────────────────────────────────
// Grantable groups per type — mirrors the backend's allowedPagesForType, which
// also filters server-side, so nothing outside the set can stick. Partner-org
// accounts (org_code ≠ SPEARMGMT) never get My Profile.
const pageGroupsFor = (user, sets) => {
  const spearOnly = (items) =>
    user.org_code && user.org_code !== "SPEARMGMT" ? (items || []).filter((p) => p !== "MyProfile") : items || [];
  if (user.user_type === "CAM") {
    return [
      { label: "Report pages", items: spearOnly(sets.reportPages) },
      { label: "Admin capabilities (scoped to their suppliers/retailers)", items: sets.camAdminPages, labels: { RepRouting: "Rep Routing (view)", DataStatus: "Data Status", Reports: "Batch Reports" } },
    ];
  }
  if (user.user_type === "org_admin") {
    // Two groups, like the CAM popover — one flat "Pages" list hid the fact that
    // Users / Articles / Batch Reports / History / Alerts are admin screens.
    // Per-org extras (constants/pages.js ORG_EXTRA_PAGES — Blue Mango's Imports +
    // Data Status); the backend applies the same cap on save.
    const extras = (sets.orgExtraPages || {})[String(user.org_code || "").toUpperCase()] || []
    const all = [...spearOnly(sets.orgAdminPages), ...extras.filter((p) => !(sets.orgAdminPages || []).includes(p))];
    const reports = all.filter((p) => (sets.reportPages || []).includes(p));
    const admin = all.filter((p) => !(sets.reportPages || []).includes(p));
    return [
      { label: "Report pages", items: reports },
      { label: "Organisation admin pages (scoped to the organisation)", items: admin, labels: ORG_ADMIN_PAGE_LABELS },
    ];
  }
  if (user.user_type === "admin") return [{ label: "Pages", items: sets.pages }];
  return [{ label: "Report pages", items: spearOnly(sets.reportPages) }];
};

// How the org-admin admin pages read on screen: the Reports grant IS the org
// admins' "Batch Reports" item, LoginHistory is the "History" item.
const ORG_ADMIN_PAGE_LABELS = { Reports: "Batch Reports", LoginHistory: "History", Users: "Users", Articles: "Articles", Alerts: "Alerts", Imports: "Imports", DataStatus: "Data Status" };

// Pages whose grant carries a read-only / read+write level (2026-09-17).
// Served by get-user-pageAccess as `levelled_pages`; Articles is the default
// so a stale backend still shows the control.
const DEFAULT_LEVELLED_PAGES = ["Articles"];

export const PagesPopover = ({ user, pageSets, onSaved }) => {
  const [selected, setSelected] = useState([]);
  const [levels, setLevels] = useState({}); // { Articles: 'ro' } — absent = read+write
  const [levelledPages, setLevelledPages] = useState(DEFAULT_LEVELLED_PAGES);
  const fetchGrants = async () => {
    const res = await axios.post(`${API}/admin/get-user-pageAccess`, { userid: user.userid });
    setSelected((res.data?.data?.page_access || []).map((p) => p.page_name));
    setLevels(res.data?.data?.page_levels || {});
    if (Array.isArray(res.data?.data?.levelled_pages) && res.data.data.levelled_pages.length) setLevelledPages(res.data.data.levelled_pages);
  };
  const save = async () => {
    // Every levelled page is sent explicitly ('ro' | 'rw') so the backend can
    // tell "read+write" from "an older frontend that sent nothing".
    const page_levels = {};
    levelledPages.forEach((p) => {
      page_levels[p] = levels[p] === "ro" ? "ro" : "rw";
    });
    await axios.post(`${API}/admin/upd-user-pageAccess`, { userid: user.userid, pages: selected, page_levels });
    if (onSaved) onSaved();
  };
  const toggle = (p) => setSelected((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  const setLevel = (p, level) => setLevels((prev) => ({ ...prev, [p]: level }));
  // Masters are never read-only; the control is for CAMs and org admins.
  const canLevel = user.user_type === "CAM" || user.user_type === "org_admin";

  return (
    <GrantPopover trigger={<Button size="xs" variant="ghost">Pages</Button>} header={`Page access — ${user.fname}`} onOpenFetch={fetchGrants} onSave={save}>
      {pageGroupsFor(user, pageSets).map((group) => (
        <Box key={group.label} mb={2}>
          <Text fontSize="xs" color="ink.500" mb={1}>{group.label}</Text>
          <Box>
            {(group.items || []).map((p) => (
              <TinyToggle key={p} on={selected.includes(p)} onClick={() => toggle(p)}>{(group.labels && group.labels[p]) || p}</TinyToggle>
            ))}
          </Box>
        </Box>
      ))}
      {canLevel &&
        levelledPages
          .filter((p) => selected.includes(p))
          .map((p) => (
            <Box key={`level-${p}`} mb={2} pt={2} borderTop="1px solid" borderColor="gray.100">
              <Text fontSize="xs" color="ink.500" mb={1}>{p} access</Text>
              <Flex gap={1}>
                <Button size="xs" variant={levels[p] === "ro" ? "solid" : "outline"} colorScheme={levels[p] === "ro" ? "orange" : "gray"} onClick={() => setLevel(p, "ro")}>
                  Read only
                </Button>
                <Button size="xs" variant={levels[p] !== "ro" ? "solid" : "outline"} colorScheme={levels[p] !== "ro" ? "green" : "gray"} onClick={() => setLevel(p, "rw")}>
                  Read + write
                </Button>
              </Flex>
              <Text fontSize="xs" color="ink.400" mt={1}>
                {levels[p] === "ro" ? "Can view the list; cannot edit brand, description, cost or the active switch." : "Can view and edit — except suppliers managed by Spear Management, which only masters edit."}
              </Text>
            </Box>
          ))}
    </GrantPopover>
  );
};

// ── Organisations (suppliers only) ───────────────────────────────────────────
// Home organisation + the extra organisations the supplier is also visible in
// (2026-09-17). Moving the home org moves the supplier's login with it; adding
// an org grants the supplier to that org's admins, removing one revokes it from
// the org's accounts (backend: /admin/upd-supplier-orgs).
export const OrgsPopover = ({ user, organisations, onSaved }) => {
  const toast = useToast();
  const current = Array.isArray(user.supplier_org_codes) && user.supplier_org_codes.length ? user.supplier_org_codes : user.org_code ? [user.org_code] : [];
  const [home, setHome] = useState(current[0] || "SPEARMGMT");
  const [extras, setExtras] = useState(current.slice(1));
  const orgs = Array.isArray(organisations) ? organisations : [];
  const isDemo = String(user.owned_suppliercode || "").toUpperCase() === "DEMO";

  const reset = async () => {
    setHome(current[0] || "SPEARMGMT");
    setExtras(current.slice(1));
  };
  const save = async () => {
    const res = await axios.post(`${API}/admin/upd-supplier-orgs`, {
      suppliercode: user.owned_suppliercode,
      home_org_code: home,
      extra_org_codes: extras.filter((c) => c !== home),
    });
    toast({ title: res.data?.title || "Organisations updated", description: res.data?.message, status: "success", duration: 4000, isClosable: true });
    if (onSaved) onSaved();
  };
  const toggleExtra = (code) => setExtras((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  const label = current.length ? `${current[0]}${current.length > 1 ? ` +${current.length - 1}` : ""}` : "—";

  if (!user.owned_suppliercode) return <Text fontSize="xs" color="ink.300">—</Text>;
  return (
    <GrantPopover
      trigger={<Button size="xs" variant="ghost" fontWeight="600" title={current.join(", ")}>{label}</Button>}
      header={`Organisations — ${user.owned_suppliercode}`}
      onOpenFetch={reset}
      onSave={save}
      width="360px"
      placement="bottom-start"
    >
      {isDemo ? (
        <Text fontSize="xs" color="ink.500">DEMO stays in Spear Management — only the master admins and the demo login may hold it.</Text>
      ) : (
        <>
          <FormControl mb={2}>
            <FormLabel fontSize="xs" mb={1}>Home organisation</FormLabel>
            <Select size="xs" value={home} onChange={(e) => setHome(e.target.value)}>
              {orgs.map((o) => (
                <option key={o.org_code} value={o.org_code}>{o.org_name} ({o.org_code})</option>
              ))}
            </Select>
            <Text fontSize="xs" color="ink.400" mt={1}>Owns the supplier and its login; the login's retailers are capped by this organisation.</Text>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>Also visible in</FormLabel>
            <Box>
              {orgs.filter((o) => o.org_code !== home).map((o) => (
                <TinyToggle key={o.org_code} on={extras.includes(o.org_code)} onClick={() => toggleExtra(o.org_code)}>{o.org_code}</TinyToggle>
              ))}
              {orgs.filter((o) => o.org_code !== home).length === 0 && <Text fontSize="xs" color="ink.400">No other organisations.</Text>}
            </Box>
            <Text fontSize="xs" color="ink.400" mt={1}>That organisation's admins receive the supplier; removing it takes it away from them again.</Text>
          </FormControl>
        </>
      )}
    </GrantPopover>
  );
};

// ── Retailers ────────────────────────────────────────────────────────────────
const RETAILERS = ["PNP", "CHK", "DIS", "MAK", "CLI", "MED", "PET"];

export const RetailersPopover = ({ user, onSaved }) => {
  const [access, setAccess] = useState({});
  const [defaultRetailer, setDefaultRetailer] = useState("");
  const fetchGrants = async () => {
    const res = await axios.post(`${API}/admin/get-user-retailerAccess`, { userid: user.userid });
    const { retailer_access, user: u } = res.data.data;
    setAccess(retailer_access.length ? retailer_access[0] : {});
    setDefaultRetailer(u.default_retailer || "");
  };
  const save = async () => {
    await axios.post(`${API}/admin/upd-user-retailerAccess`, { userid: user.userid, default_retailer: defaultRetailer, retailerAccess: access });
    if (onSaved) onSaved();
  };
  const toggle = (r) => setAccess((prev) => ({ ...prev, [r]: !prev[r] }));

  return (
    <GrantPopover trigger={<Button size="xs" variant="ghost">Retailers</Button>} header={`Retailer access — ${user.fname}`} onOpenFetch={fetchGrants} onSave={save}>
      <Box mb={2}>
        {RETAILERS.map((r) => (
          <TinyToggle key={r} on={!!access[r.toLowerCase()]} onClick={() => toggle(r.toLowerCase())}>{r}</TinyToggle>
        ))}
      </Box>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Default retailer</FormLabel>
        <Select size="xs" value={defaultRetailer || ""} onChange={(e) => setDefaultRetailer(e.target.value)}>
          <option value="">—</option>
          {RETAILERS.filter((r) => access[r.toLowerCase()]).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </Select>
      </FormControl>
    </GrantPopover>
  );
};

// ── Suppliers ────────────────────────────────────────────────────────────────
export const SuppliersPopover = ({ user, onSaved }) => {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [defaultSupplier, setDefaultSupplier] = useState("");
  const fetchGrants = async () => {
    const res = await axios.post(`${API}/admin/get-user-supplierAccess`, { userid: user.userid });
    const { supplier_access, suppliers, user: u } = res.data.data;
    setSelected(supplier_access.map((s) => s.suppliercode));
    setAvailable(suppliers.map((s) => s.suppliercode));
    setDefaultSupplier(u.default_supplier || "");
  };
  const save = async () => {
    await axios.post(`${API}/admin/upd-user-supplierAccess`, { userid: user.userid, default_supplier: defaultSupplier, suppliers: selected });
    if (onSaved) onSaved();
  };
  const toggle = (s) => setSelected((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <GrantPopover trigger={<Button size="xs" variant="ghost">Suppliers</Button>} header={`Supplier access — ${user.fname}`} onOpenFetch={fetchGrants} onSave={save} width="420px">
      <Box maxH="260px" overflowY="auto" mb={2}>
        {available.map((s) => (
          <TinyToggle key={s} on={selected.includes(s)} onClick={() => toggle(s)}>{s}</TinyToggle>
        ))}
      </Box>
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Default supplier</FormLabel>
        <Select size="xs" value={defaultSupplier || ""} onChange={(e) => setDefaultSupplier(e.target.value)}>
          <option value="">—</option>
          {selected.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Select>
      </FormControl>
    </GrantPopover>
  );
};

// ── Billing ──────────────────────────────────────────────────────────────────
// Amount is stored in cents (users.subscription_amount); edited in Rands here.
export const BillingPopover = ({ user, trigger, onSaved }) => {
  const [amount, setAmount] = useState(((Number(user.subscription_amount) || 0) / 100).toString());
  const save = async () => {
    await axios.post(`${API}/admin/upd-subscription_amount`, { userid: user.userid, subscription_amount: Math.round((parseFloat(amount) || 0) * 100) });
    if (onSaved) onSaved();
  };
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

  return (
    <GrantPopover trigger={trigger} header={`Billing — ${user.fname}`} onSave={save} width="280px">
      <FormControl mb={2}>
        <FormLabel fontSize="xs" mb={1}>Monthly amount</FormLabel>
        <InputGroup size="xs">
          <InputLeftAddon>R</InputLeftAddon>
          <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </InputGroup>
      </FormControl>
      <Text fontSize="xs" color="ink.500">Last paid: {fmt(user.date_last_paid)}</Text>
      <Text fontSize="xs" color="ink.500">Subscribed since: {fmt(user.subscription_sdate)}</Text>
      <Text fontSize="xs" color="ink.500">Account created: {fmt(user.createddate)}</Text>
    </GrantPopover>
  );
};
